import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FolderArchive, ArrowLeft, Upload, Lock, Loader2, Eye, Trash2, X,
  FileText, Image as ImageIcon, ChevronRight, Search, Building,
  History as HistoryIcon, AlertTriangle, FileSignature, Wallet,
  CalendarOff, Plane, Scroll, Briefcase,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '@/utils/api';

const CAT_ICON = {
  contratos_anexos: FileSignature,
  remuneraciones_haberes: Wallet,
  ausencias_licencias: CalendarOff,
  vacaciones: Plane,
  finanzas_internas: Briefcase,
  historial_operativo_legal: Scroll,
};
const CAT_COLOR = {
  contratos_anexos: 'indigo',
  remuneraciones_haberes: 'emerald',
  ausencias_licencias: 'amber',
  vacaciones: 'sky',
  finanzas_internas: 'violet',
  historial_operativo_legal: 'rose',
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-CL', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};
const fmtSize = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function RepositorioDocumentalTab() {
  const [status, setStatus] = useState(null);
  const [funcionarios, setFuncionarios] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, f] = await Promise.all([
        api.get('/repositorio-rrhh/status'),
        api.get('/repositorio-rrhh/funcionarios').catch(() => ({ data: { items: [] } })),
      ]);
      setStatus(s.data);
      setFuncionarios(f.data?.items || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        setStatus({ autorizado: false, usuarios_autorizados: err.response.data?.usuarios_autorizados || [] });
      } else {
        toast.error(formatErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return funcionarios;
    return funcionarios.filter(f =>
      (f.name || '').toLowerCase().includes(q) ||
      (f.email || '').toLowerCase().includes(q) ||
      (f.cargo || '').toLowerCase().includes(q) ||
      (f.area || '').toLowerCase().includes(q) ||
      (f.rut || '').toLowerCase().includes(q)
    );
  }, [funcionarios, search]);

  if (loading && !status) {
    return <Card className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></Card>;
  }
  if (status && !status.autorizado) {
    return (
      <Card className="p-12 text-center border-rose-200 bg-rose-50" data-testid="repo-acceso-denegado">
        <Lock className="w-12 h-12 text-rose-400 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-rose-900">Acceso restringido</h3>
        <p className="text-sm text-rose-700 mt-2">
          Este repositorio documental requiere el permiso <strong className="font-mono">rrhh_avanzado</strong> o ser Socio de la firma.<br />
          Solicita acceso a través del Panel de Control.
        </p>
      </Card>
    );
  }

  if (selectedEmail) {
    return (
      <CarpetaIndividual
        email={selectedEmail}
        status={status}
        onBack={() => { setSelectedEmail(null); load(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="p-4 border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
              <FolderArchive className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Repositorio Documental</h2>
              <p className="text-xs text-slate-500">
                Carpeta Digital del Funcionario · 6 categorías · solo PDF/PNG/JPG hasta {status?.max_file_mb || 20} MB · acceso auditado
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar funcionario, RUT, área…"
                className="pl-8 w-64"
                data-testid="repo-search-input"
              />
            </div>
            <Button variant="outline" onClick={() => setAuditOpen(true)} data-testid="repo-audit-btn">
              <HistoryIcon className="w-4 h-4 mr-1" /> Auditoría
            </Button>
          </div>
        </div>
      </Card>

      {/* Grid funcionarios */}
      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-slate-500">
          <Building className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">No hay funcionarios que coincidan con la búsqueda.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="repo-funcionarios-grid">
          {filtered.map(f => (
            <button
              key={f.email}
              onClick={() => setSelectedEmail(f.email)}
              className="text-left p-4 rounded-lg border border-slate-200 bg-white hover:border-indigo-400 hover:shadow-sm transition group"
              data-testid={`repo-func-${f.email}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-slate-900 truncate">{f.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{f.email}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {f.area && <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded">{f.area}</span>}
                    {f.cargo && <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded">{f.cargo}</span>}
                  </div>
                  {f.rut && <div className="text-[10px] text-slate-400 mt-1">RUT {f.rut}</div>}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-indigo-600 tabular-nums">{f.total_docs}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wide">docs</div>
                </div>
              </div>
              <div className="mt-2 text-[11px] text-indigo-600 flex items-center group-hover:gap-1.5 gap-1 transition-all">
                Abrir carpeta digital <ChevronRight className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      )}

      {auditOpen && (
        <AuditoriaDialog onClose={() => setAuditOpen(false)} />
      )}
    </div>
  );
}


// =============================================================
// CARPETA INDIVIDUAL DEL FUNCIONARIO
// =============================================================

function CarpetaIndividual({ email, status, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadCat, setUploadCat] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const categorias = status?.categorias || [];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/repositorio-rrhh/funcionarios/${encodeURIComponent(email)}/documentos`);
      setData(r.data);
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return <Card className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></Card>;
  }

  const f = data.funcionario || {};

  return (
    <div className="space-y-4">
      <Card className="p-4 border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack} data-testid="repo-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Volver
            </Button>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{f.name}</h2>
              <p className="text-xs text-slate-500">
                {f.email}
                {f.area ? ` · ${f.area}` : ''}
                {f.cargo ? ` · ${f.cargo}` : ''}
                {f.rut ? ` · RUT ${f.rut}` : ''}
              </p>
            </div>
          </div>
          <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
            {data.total} documento{data.total === 1 ? '' : 's'}
          </Badge>
        </div>
      </Card>

      <Accordion type="multiple" defaultValue={categorias.map(c => c.id)} className="space-y-2">
        {categorias.map(cat => {
          const Icon = CAT_ICON[cat.id] || FolderArchive;
          const color = CAT_COLOR[cat.id] || 'indigo';
          const docs = data.grupos?.[cat.id] || [];
          return (
            <AccordionItem
              key={cat.id}
              value={cat.id}
              className={`border border-slate-200 rounded-lg bg-white px-3`}
              data-testid={`repo-accordion-${cat.id}`}
            >
              <AccordionTrigger className="hover:no-underline py-3">
                <div className="flex items-center gap-3 flex-1">
                  <div className={`w-9 h-9 rounded-lg bg-${color}-100 text-${color}-700 flex items-center justify-center`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-sm font-bold text-slate-900">{cat.nombre}</div>
                    <div className="text-[11px] text-slate-500 line-clamp-1">{cat.descripcion}</div>
                  </div>
                  <Badge variant="outline" className="ml-2">{docs.length}</Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-3">
                <div className="flex items-center justify-end mb-2">
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setUploadCat(cat); }}
                    className="bg-indigo-600 hover:bg-indigo-700 h-7 text-xs"
                    data-testid={`repo-upload-btn-${cat.id}`}
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" /> Subir Documento
                  </Button>
                </div>
                {docs.length === 0 ? (
                  <div className="py-6 text-center text-xs text-slate-400 border border-dashed border-slate-200 rounded">
                    Sin documentos en esta carpeta todavía.
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-700">
                      <tr>
                        <th className="text-left px-2 py-1.5">Documento</th>
                        <th className="text-left px-2 py-1.5">Archivo original</th>
                        <th className="text-left px-2 py-1.5">Fecha</th>
                        <th className="text-left px-2 py-1.5">Subido por</th>
                        <th className="text-right px-2 py-1.5">Tamaño</th>
                        <th className="px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {docs.map(d => (
                        <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`repo-doc-row-${d.id}`}>
                          <td className="px-2 py-1.5">
                            <button
                              onClick={() => setPreviewDoc(d)}
                              className="font-semibold text-indigo-700 hover:underline inline-flex items-center gap-1"
                              data-testid={`repo-doc-open-${d.id}`}
                            >
                              {(d.mime || '').startsWith('image/')
                                ? <ImageIcon className="w-3.5 h-3.5" />
                                : <FileText className="w-3.5 h-3.5" />}
                              {d.glosa}
                            </button>
                          </td>
                          <td className="px-2 py-1.5 text-slate-500 truncate max-w-[200px]" title={d.nombre_original}>
                            {d.nombre_original}
                          </td>
                          <td className="px-2 py-1.5 text-slate-600">{fmtDateTime(d.uploaded_at)}</td>
                          <td className="px-2 py-1.5 text-slate-600">{d.uploaded_by_name}</td>
                          <td className="px-2 py-1.5 text-right text-slate-500 tabular-nums">{fmtSize(d.size)}</td>
                          <td className="px-2 py-1.5 text-right">
                            <div className="inline-flex gap-1">
                              <button
                                onClick={() => setPreviewDoc(d)}
                                className="p-1 text-slate-400 hover:text-indigo-600"
                                title="Ver"
                                data-testid={`repo-doc-eye-${d.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`¿Eliminar "${d.glosa}"? Esta acción quedará registrada en auditoría.`)) return;
                                  try {
                                    await api.delete(`/repositorio-rrhh/documentos/${d.id}`);
                                    toast.success('Documento eliminado');
                                    load();
                                  } catch (err) { toast.error(formatErrorMessage(err)); }
                                }}
                                className="p-1 text-slate-400 hover:text-rose-600"
                                title="Eliminar"
                                data-testid={`repo-doc-delete-${d.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {uploadCat && (
        <UploadDialog
          email={email}
          categoria={uploadCat}
          maxMB={status?.max_file_mb || 20}
          onClose={() => setUploadCat(null)}
          onSuccess={() => { setUploadCat(null); load(); }}
        />
      )}
      {previewDoc && (
        <PreviewDialog doc={previewDoc} onClose={() => setPreviewDoc(null)} />
      )}
    </div>
  );
}


// =============================================================
// DIALOG: UPLOAD (DRAG & DROP)
// =============================================================

function UploadDialog({ email, categoria, maxMB, onClose, onSuccess }) {
  const [glosa, setGlosa] = useState('');
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const onPick = (f) => {
    if (!f) return;
    const validExt = /\.(pdf|png|jpe?g)$/i.test(f.name || '');
    if (!validExt) { toast.error('Sólo PDF, PNG o JPG'); return; }
    if (f.size > maxMB * 1024 * 1024) { toast.error(`Archivo > ${maxMB} MB`); return; }
    setFile(f);
  };

  const submit = async () => {
    if (!file) { toast.error('Selecciona un archivo'); return; }
    if (!glosa.trim()) { toast.error('La glosa/descripción es obligatoria'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('categoria', categoria.id);
      fd.append('glosa', glosa.trim());
      fd.append('file', file);
      await api.post(
        `/repositorio-rrhh/funcionarios/${encodeURIComponent(email)}/documentos`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Documento cargado');
      onSuccess();
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="repo-modal-upload">
        <DialogHeader>
          <DialogTitle>Subir documento — {categoria.nombre}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-500">{categoria.descripcion}</p>
          <div>
            <Label>Glosa / descripción *</Label>
            <Input
              value={glosa}
              onChange={(e) => setGlosa(e.target.value)}
              placeholder="Ej: Anexo aumento sueldo enero 2026"
              data-testid="repo-upload-glosa"
            />
          </div>
          <div>
            <Label>Archivo (PDF / PNG / JPG, máx {maxMB} MB) *</Label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => {
                e.preventDefault(); setDrag(false);
                const f = e.dataTransfer?.files?.[0];
                onPick(f);
              }}
              onClick={() => inputRef.current?.click()}
              className={`mt-1 cursor-pointer border-2 border-dashed rounded-lg p-6 text-center transition ${drag ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'}`}
              data-testid="repo-upload-dropzone"
            >
              <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
              {file ? (
                <div>
                  <div className="text-sm font-semibold text-slate-800">{file.name}</div>
                  <div className="text-[11px] text-slate-500">{fmtSize(file.size)}</div>
                </div>
              ) : (
                <div className="text-xs text-slate-500">
                  Arrastra el archivo aquí o haz clic para seleccionarlo
                </div>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                className="hidden"
                onChange={(e) => onPick(e.target.files?.[0])}
                data-testid="repo-upload-input"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || !file || !glosa.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="repo-upload-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar documento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =============================================================
// DIALOG: PREVIEW (LIGHTBOX)
// =============================================================

function PreviewDialog({ doc, onClose }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(null);
  const isImage = (doc.mime || '').startsWith('image/');

  useEffect(() => {
    let active = true;
    let objectUrl = null;
    (async () => {
      try {
        const r = await api.get(`/repositorio-rrhh/documentos/${doc.id}/file`, { responseType: 'blob' });
        if (!active) return;
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      } catch (e) {
        if (active) setErr(formatErrorMessage(e));
      }
    })();
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [doc.id]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden" data-testid="repo-modal-preview">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-900 truncate">{doc.glosa}</div>
            <div className="text-[11px] text-slate-500 truncate">{doc.nombre_original} · {fmtDateTime(doc.uploaded_at)} · {doc.uploaded_by_name}</div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="repo-preview-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="bg-slate-900" style={{ height: '75vh' }}>
          {err ? (
            <div className="h-full flex items-center justify-center text-rose-300 text-sm gap-2">
              <AlertTriangle className="w-5 h-5" /> {err}
            </div>
          ) : !src ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : isImage ? (
            <div className="h-full flex items-center justify-center overflow-auto p-4">
              <img src={src} alt={doc.glosa} className="max-h-full max-w-full object-contain" />
            </div>
          ) : (
            <iframe src={src} title={doc.glosa} className="w-full h-full bg-white" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// =============================================================
// DIALOG: AUDITORÍA
// =============================================================

function AuditoriaDialog({ onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/repositorio-rrhh/auditoria');
        setRows(r.data?.items || []);
      } catch (err) {
        toast.error(formatErrorMessage(err));
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="repo-modal-audit">
        <DialogHeader>
          <DialogTitle>Auditoría · últimos {rows.length} eventos</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto">
          {loading ? (
            <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-indigo-500" /></div>
          ) : rows.length === 0 ? (
            <p className="text-center text-sm text-slate-500 py-8">Sin eventos registrados.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-700 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5">Fecha</th>
                  <th className="text-left px-2 py-1.5">Acción</th>
                  <th className="text-left px-2 py-1.5">Por</th>
                  <th className="text-left px-2 py-1.5">Funcionario</th>
                  <th className="text-left px-2 py-1.5">Documento</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(a => (
                  <tr key={a.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 text-slate-600">{fmtDateTime(a.at)}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${a.accion === 'eliminar' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {a.accion}
                      </span>
                    </td>
                    <td className="px-2 py-1.5">{a.by_name} <span className="text-slate-400 text-[10px]">({a.by_email})</span></td>
                    <td className="px-2 py-1.5">{a.payload?.empleado_email}</td>
                    <td className="px-2 py-1.5">{a.payload?.glosa} <span className="text-slate-400">· {a.payload?.nombre_original}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
